import { LegalPageLayout, P, Strong } from './LegalPageLayout';

interface Subprocessor {
  name: string;
  purpose: string;
  data: string;
  region: string;
  url: string;
}

/** Core Sub-processors engaged for all customers. */
const CORE: Subprocessor[] = [
  {
    name: 'Cloudflare R2',
    purpose: 'Encrypted object storage for uploaded documents, exports, and attachments',
    data: 'Encrypted file blobs (invoices, receipts, contracts) and object metadata',
    region: 'Global; EU-jurisdiction bucket option (R2 EU jurisdiction)',
    url: 'https://www.cloudflare.com/privacypolicy/',
  },
  {
    name: 'Amazon SES',
    purpose: 'Transactional email delivery (verification, notifications, billing receipts)',
    data: 'Recipient email address, subject, and body of system emails',
    region: 'EU / Tokyo region (per configuration)',
    url: 'https://aws.amazon.com/privacy/',
  },
  {
    name: 'Stripe, Inc.',
    purpose: 'Subscription billing and corporate card issuing',
    data: 'Billing contact, payment-method tokens, card issuance references',
    region: 'United States + Ireland (EEA)',
    url: 'https://stripe.com/privacy',
  },
  {
    name: 'Wise (TransferWise)',
    purpose: 'Cross-border accounts-payable payouts',
    data: 'Counterparty banking details and payment instructions',
    region: 'United Kingdom + EEA',
    url: 'https://wise.com/privacy-policy',
  },
  {
    name: 'OpenRouter',
    purpose: 'AI inference gateway routing requests to underlying model providers',
    data: 'Document text and structured metadata submitted for AI extraction and classification',
    region: 'Multi-region routing (pinnable on Enterprise)',
    url: 'https://openrouter.ai/privacy',
  },
  {
    name: 'Qdrant',
    purpose: 'Vector search and semantic matching index',
    data: 'Vector embeddings and document/record identifiers',
    region: 'EU-hosted',
    url: 'https://qdrant.tech/legal/privacy-policy/',
  },
  {
    name: 'Hetzner Online GmbH',
    purpose: 'Primary cloud hosting (compute + database)',
    data: 'All Customer Data processed by the Service',
    region: 'Germany / EU',
    url: 'https://www.hetzner.com/legal/privacy-policy',
  },
];

/** Optional Sub-processors engaged only when a related feature is enabled. */
const OPTIONAL: Subprocessor[] = [
  {
    name: 'GMO Payment Gateway, Inc.',
    purpose: 'Japan-domestic corporate card issuance (when enabled)',
    data: 'Cardholder name and billing reference',
    region: 'Japan',
    url: 'https://corp.gmo-pg.com/privacy/',
  },
  {
    name: 'Peppol AS4 access-point partner (e.g. Storecove / Pagero)',
    purpose: 'Sending and receiving Peppol e-invoices over the AS4 network (when enabled)',
    data: 'Invoice content and counterparty identifiers in UBL/JP PINT documents',
    region: 'EU',
    url: 'https://www.storecove.com/privacy',
  },
  {
    name: 'Accredited RFC 3161 TSA (e.g. セコム / 三菱電機)',
    purpose: 'Trusted timestamping for document integrity under 電子帳簿保存法 (when enabled)',
    data: 'Document hash values only (no document content)',
    region: 'Japan',
    url: 'https://www.secomtrust.net/',
  },
];

function Table({ rows }: { rows: Subprocessor[] }) {
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm border border-border rounded-2xl overflow-hidden">
        <thead>
          <tr className="bg-muted/40 text-left">
            <th className="px-3 py-2 font-medium">Sub-processor</th>
            <th className="px-3 py-2 font-medium">Purpose</th>
            <th className="px-3 py-2 font-medium">Data</th>
            <th className="px-3 py-2 font-medium">Region</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.name} className="border-t border-border align-top">
              <td className="px-3 py-2 font-medium">
                <a href={s.url} target="_blank" rel="noreferrer" className="text-accent-700 underline underline-offset-2 hover:text-accent-800">
                  {s.name}
                </a>
              </td>
              <td className="px-3 py-2 text-foreground/80">{s.purpose}</td>
              <td className="px-3 py-2 text-foreground/80">{s.data}</td>
              <td className="px-3 py-2 text-foreground/80">{s.region}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SubprocessorsPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.subprocessors"
      lastUpdated="2026-06-03"
      lead={
        <>
          This page lists every third-party sub-processor PlyEZ engages to provide
          the Service, together with the purpose, the categories of data involved,
          and the processing region for each. Each sub-processor is bound by a
          data-processing agreement no less protective than the obligations in our{' '}
          <a className="text-accent-700 underline" href="/legal/dpa">Data Processing Addendum</a>.
        </>
      }
      sections={[
        {
          id: 'core',
          title: 'Current sub-processors',
          body: (
            <>
              <P>
                The following sub-processors are engaged for all customers as part of
                the core Service.
              </P>
              <Table rows={CORE} />
            </>
          ),
        },
        {
          id: 'optional',
          title: 'Optional sub-processors (when enabled)',
          body: (
            <>
              <P>
                The following sub-processors are engaged only when the related feature
                is enabled for a given customer or workspace.
              </P>
              <Table rows={OPTIONAL} />
            </>
          ),
        },
        {
          id: 'changes',
          title: 'Changes and notifications',
          body: (
            <>
              <P>
                We will notify customers at least <Strong>30 days</Strong> before
                adding or replacing a sub-processor, via in-app notification and email,
                so that customers have time to review and, where appropriate, object on
                reasonable data-protection grounds (see Section&nbsp;6 of the DPA).
              </P>
              <P>
                To subscribe to sub-processor change notifications — including if you do
                not use PlyEZ directly — email{' '}
                <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
                and we will add you to the notification list.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}
